const LINKED_TRANSCRIPT_JOB_ACTIONS = new Set(["preview", "build", "deploy"]);
const SYNTHETIC_REQUEST_ACTIONS = new Set([
  "deploy",
  "status",
  "build",
  "preview",
  "test",
  "db_status",
  "db_migrate",
  "db_seed",
  "db_logs",
  "db_start",
  "db_stop",
  "rollback_release"
]);
const SYNTHETIC_EVENT_REQUEST_ACTIONS = new Set(["rollback_release"]);
SYNTHETIC_EVENT_REQUEST_ACTIONS.add("save_file");
SYNTHETIC_EVENT_REQUEST_ACTIONS.add("revert");

function itemTimestamp(value) {
  return new Date(value || 0).getTime();
}

function transcriptSortRank(item) {
  if (item.type === "request") return 0;
  if (item.type === "prompt") return 1;
  if (item.type === "job") return 2;
  if (item.type === "event") return 3;
  return 4;
}

function runTurnId(run) {
  return run?.turn_id || `prompt-${run?.id}`;
}

function actionRequestText(action = "") {
  if (action === "create_project") return "Voce iniciou a criacao manual de um novo projeto.";
  if (action === "deploy") return "Voce iniciou uma publicacao manual.";
  if (action === "status") return "Voce iniciou uma verificacao do projeto.";
  if (action === "build") return "Voce iniciou um build manual.";
  if (action === "preview") return "Voce iniciou uma atualizacao manual do preview.";
  if (action === "test") return "Voce iniciou uma validacao manual.";
  if (action === "save_file") return "Voce iniciou o salvamento manual do arquivo.";
  if (action === "revert") return "Voce iniciou a reversao manual da alteracao.";
  if (action === "db_status") return "Voce iniciou uma verificacao do banco.";
  if (action === "db_migrate") return "Voce iniciou a aplicacao de migracoes no banco.";
  if (action === "db_seed") return "Voce iniciou a carga de dados no banco.";
  if (action === "db_logs") return "Voce iniciou a consulta dos logs do banco.";
  if (action === "db_start") return "Voce iniciou o banco local.";
  if (action === "db_stop") return "Voce iniciou a parada do banco local.";
  if (action === "rollback_release") return "Voce iniciou a republicacao da versao anterior.";
  return "Voce iniciou uma acao manual.";
}

function shouldSynthesizeRequest(job) {
  const action = job?.action || "";
  if (job?.request_mode === "none") return false;
  if (!SYNTHETIC_REQUEST_ACTIONS.has(action)) return false;
  if (job?.owner_turn_id) return false;
  if (job?.origin_kind === "prompt_run" && job?.origin_id != null) return false;
  return job?.transcript_mode === "standalone";
}

function shouldSynthesizeEventRequest(event) {
  const action = event?.action || "";
  return SYNTHETIC_EVENT_REQUEST_ACTIONS.has(action);
}

function shouldLinkJobToRun(job) {
  return LINKED_TRANSCRIPT_JOB_ACTIONS.has(job?.action || "");
}

function isPendingPromptRun(run) {
  if (!run || !["queued", "running"].includes(run.status || "")) return false;
  if (run.cancel_requested_at) return false;
  if (run.progress_stage === "canceling") return false;
  return true;
}

function deriveActiveRun(runs = []) {
  return [...runs]
    .filter(isPendingPromptRun)
    .sort((a, b) => {
      const statusDelta = (a.status === "running" ? 0 : 1) - (b.status === "running" ? 0 : 1);
      if (statusDelta !== 0) return statusDelta;
      const timeDelta = itemTimestamp(a.created_at || a.updated_at || a.completed_at) - itemTimestamp(b.created_at || b.updated_at || b.completed_at);
      if (timeDelta !== 0) return timeDelta;
      return String(a.id).localeCompare(String(b.id));
    })[0] || null;
}

function deriveActiveActionJob(actionJobs = []) {
  return [...actionJobs]
    .filter((job) => ["queued", "running"].includes(job?.status || "") && !job?.cancel_requested_at)
    .sort((a, b) => itemTimestamp(a.created_at || a.updated_at || a.completed_at) - itemTimestamp(b.created_at || b.updated_at || b.completed_at))[0] || null;
}

export function buildActionJobTranscriptEntries(job) {
  if (!job?.id) return [];
  const turnId = job.turn_id || `action-job-${job.id}`;
  const time = job.created_at || job.updated_at || job.completed_at || new Date().toISOString();
  const entries = [];
  if (shouldSynthesizeRequest(job)) {
    entries.push({
      id: `request-${turnId}`,
      type: "request",
      time,
      item: {
        action: job.action,
        text: actionRequestText(job.action),
        sourceJobId: job.id
      }
    });
  }
  entries.push({
    id: turnId,
    type: "job",
    time,
    item: {
      ...job,
      turn_id: turnId
    }
  });
  return entries;
}

function eventTranscriptTitle(action = "") {
  if (action === "create_project") return "Projeto criado";
  if (action === "rollback_release") return "Versão republicada";
  if (action === "save_file") return "Arquivo salvo";
  if (action === "revert") return "Alteração revertida";
  return "Operação";
}

export function buildManualActionEventTranscriptEntries({
  action = "",
  ok = true,
  message = "",
  detail = "",
  createdAt = new Date().toISOString()
} = {}) {
  if (!action) return [];
  const idSuffix = `${action}-${String(createdAt).replace(/\W/g, "") || Date.now()}`;
  const eventId = `manual-event-${idSuffix}`;
  const entries = [];
  if (shouldSynthesizeEventRequest({ action })) {
    entries.push({
      id: `request-${eventId}`,
      type: "request",
      time: createdAt,
      item: {
        action,
        text: actionRequestText(action),
        sourceEventId: eventId
      }
    });
  }
  entries.push({
    id: eventId,
    type: "event",
    time: createdAt,
    item: {
      id: eventId,
      action,
      ok,
      created_at: createdAt,
      activityItem: {
        id: eventId,
        title: eventTranscriptTitle(action),
        status: ok ? "completed" : "failed",
        statusLabel: ok ? "Concluído" : "Falhou",
        summary: message || "Operação registrada.",
        detail
      }
    }
  });
  return entries;
}

export function mergeTranscriptItems(existing = [], incoming = []) {
  const next = [...existing];
  const indexById = new Map(next.map((entry, index) => [String(entry?.id || index), index]));
  for (const entry of incoming) {
    if (!entry?.id) continue;
    const key = String(entry.id);
    if (indexById.has(key)) {
      next[indexById.get(key)] = {
        ...next[indexById.get(key)],
        ...entry,
        item: entry.item ?? next[indexById.get(key)]?.item,
        linkedJobs: entry.linkedJobs ?? next[indexById.get(key)]?.linkedJobs
      };
      continue;
    }
    indexById.set(key, next.length);
    next.push(entry);
  }
  return next.sort((a, b) => {
    const timeDelta = itemTimestamp(a?.time) - itemTimestamp(b?.time);
    if (timeDelta !== 0) return timeDelta;
    const rankDelta = transcriptSortRank(a) - transcriptSortRank(b);
    if (rankDelta !== 0) return rankDelta;
    return String(a?.id || "").localeCompare(String(b?.id || ""));
  });
}

function findOwningRunIndex(sortedRuns, job, jobTime) {
  if (job?.transcript_mode === "inline" && job?.owner_turn_id) {
    const explicitOwnerIndex = sortedRuns.findIndex((run) => runTurnId(run) === job.owner_turn_id);
    if (explicitOwnerIndex >= 0) return explicitOwnerIndex;
  }
  if (job?.origin_kind === "prompt_run" && job?.origin_id != null) {
    const explicitOwnerIndex = sortedRuns.findIndex((run) => String(run.id) === String(job.origin_id));
    if (explicitOwnerIndex >= 0) return explicitOwnerIndex;
  }
  let ownerIndex = -1;
  for (let index = 0; index < sortedRuns.length; index += 1) {
    const run = sortedRuns[index];
    const runTime = itemTimestamp(run.created_at || run.updated_at || run.completed_at);
    if (runTime > jobTime) break;
    if ((run.mode || "edit") === "plan") continue;
    ownerIndex = index;
  }
  return ownerIndex;
}

export function buildChatTranscript({
  runs = [],
  actionJobs = [],
  actionEvents = [],
  chatOverview = null,
  activeRun = null,
  activeActionJob = null,
  publication = null
} = {}) {
  const resolvedActiveRun = activeRun || deriveActiveRun(runs);
  const resolvedActiveActionJob = activeActionJob || deriveActiveActionJob(actionJobs);
  const sortedRuns = [...runs].sort(
    (a, b) => itemTimestamp(a.created_at || a.updated_at || a.completed_at) - itemTimestamp(b.created_at || b.updated_at || b.completed_at)
  );
  const promptItems = sortedRuns.map((run) => ({
    id: runTurnId(run),
    type: "prompt",
    time: run.created_at,
    item: run,
    linkedJobs: []
  }));
  const topLevelEventItems = [];
  [...actionEvents]
    .sort((a, b) => itemTimestamp(a.created_at) - itemTimestamp(b.created_at))
    .forEach((event) => {
      const explicitStandalone = event?.transcript_mode === "standalone";
      const legacyStandalone = !event?.transcript_mode && !event?.owner_turn_id && !event?.action_job_id;
      if (!explicitStandalone && !legacyStandalone) return;
      topLevelEventItems.push({
        id: event?.turn_id || `event-${event.id}`,
        type: "event",
        time: event.created_at,
        item: event,
        linkedJobs: []
      });
    });
  const ownerItemsByTurnId = new Map([
    ...promptItems.map((item) => [item.id, item]),
    ...topLevelEventItems.map((item) => [item.id, item])
  ]);
  const topLevelJobItems = [];

  [...actionJobs]
    .sort((a, b) => itemTimestamp(a.created_at || a.updated_at || a.completed_at) - itemTimestamp(b.created_at || b.updated_at || b.completed_at))
    .forEach((job) => {
      const jobTime = itemTimestamp(job.created_at || job.updated_at || job.completed_at);
      const explicitOwnerItem = job?.owner_turn_id ? ownerItemsByTurnId.get(job.owner_turn_id) : null;
      if (job?.transcript_mode === "inline" && explicitOwnerItem) {
        explicitOwnerItem.linkedJobs.push(job);
        return;
      }
      const hasExplicitTranscript = Boolean(job?.transcript_mode || job?.owner_turn_id || job?.turn_id);
      const ownerIndex = (!hasExplicitTranscript && shouldLinkJobToRun(job))
        ? findOwningRunIndex(sortedRuns, job, jobTime)
        : -1;
      if (ownerIndex >= 0 && promptItems[ownerIndex]) {
        promptItems[ownerIndex].linkedJobs.push(job);
        return;
      }
      topLevelJobItems.push({
        id: job?.turn_id || `job-${job.id}`,
        type: "job",
        time: job.created_at,
        item: job
      });
    });

  const requestItems = topLevelJobItems
    .filter((item) => shouldSynthesizeRequest(item.item))
    .map((item) => ({
      id: `request-${item.id}`,
      type: "request",
      time: item.time,
      item: {
        action: item.item.action,
        text: actionRequestText(item.item.action),
        sourceJobId: item.item.id
      }
    }));
  const requestEventItems = topLevelEventItems
    .filter((item) => shouldSynthesizeEventRequest(item.item))
    .map((item) => ({
      id: `request-${item.id}`,
      type: "request",
      time: item.time,
      item: {
        action: item.item.action,
        text: actionRequestText(item.item.action),
        sourceEventId: item.item.id
      }
    }));

  const items = [...promptItems, ...requestItems, ...requestEventItems, ...topLevelJobItems, ...topLevelEventItems].sort((a, b) => {
    const timeDelta = itemTimestamp(a.time) - itemTimestamp(b.time);
    if (timeDelta !== 0) return timeDelta;
    const rankDelta = transcriptSortRank(a) - transcriptSortRank(b);
    if (rankDelta !== 0) return rankDelta;
    return String(a.id).localeCompare(String(b.id));
  });
  const visibleTurnIds = new Set(items.map((item) => item.id));
  const validationOwnerVisible = Boolean(publication?.validation?.ownerTurnRef?.id && visibleTurnIds.has(publication.validation.ownerTurnRef.id));
  const shouldShowPublicationTurn = Boolean(chatOverview && !resolvedActiveRun && !resolvedActiveActionJob && !publication?.turnRef && !validationOwnerVisible);
  if (shouldShowPublicationTurn) {
    items.push({
      id: "publication-overview",
      type: "publication",
      time: new Date().toISOString(),
      item: {
        title: chatOverview.title,
        detail: chatOverview.detail,
        note: chatOverview.note
      }
    });
  }
  return items;
}
